import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';

import { calculateSecondsToHoursAndMinutes } from '@libs/dateTime';
import {
    TimesheetsNoDataFound,
    MonthCalendar,
    StatusLegend,
    AttendanceSummaryChips,
    MonthlySummaryTable
} from 'timeAndAttendance/components';
import {
    buildMonthDaySheet,
    summarizeDays
} from 'timeAndAttendance/statusTokens';
import { useMonthlyTimesheetsState } from './useMonthlyTimesheets';

/**
 * My monthly attendance as a calendar (one coloured cell per day, like the
 * dashboard calendar): month title + worked hours, per-status count chips,
 * the calendar grid, and the colour legend.
 */
const Content = () => {
    const { data: user, year, month } = useMonthlyTimesheetsState();
    const yearMonth = `${year}-${month}`;

    const daySheet = useMemo(
        () =>
            buildMonthDaySheet(user?.entries || [], yearMonth, {
                joiningDate: user?.joiningDate,
                resignationDate: user?.resignationDate
            }),
        [user, yearMonth]
    );
    const summary = useMemo(() => summarizeDays(daySheet), [daySheet]);

    if (!user?.email) return <TimesheetsNoDataFound />;

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 720,
                mx: 'auto',
                px: { xs: 0.5, sm: 2 },
                pb: 2
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5
                }}
            >
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    {dayjs(yearMonth).format('MMMM YYYY')}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                    Worked{' '}
                    {calculateSecondsToHoursAndMinutes(
                        user.totalDuration || 0
                    )}
                    h of{' '}
                    {calculateSecondsToHoursAndMinutes(
                        user.totalShiftDuration || 0
                    )}
                    h
                </Typography>
            </Box>

            <AttendanceSummaryChips summary={summary} sx={{ mb: 2 }} />
            <MonthCalendar daySheet={daySheet} yearMonth={yearMonth} />
            <StatusLegend sx={{ mt: 2 }} />
            <MonthlySummaryTable
                monthSheets={[
                    {
                        month: Number(month),
                        title: dayjs(yearMonth).format('MMMM YYYY'),
                        daySheet
                    }
                ]}
                monthlyTotals={user.monthlyTotals}
                sx={{ mt: 3 }}
            />
        </Box>
    );
};

export default Content;
